import type { CmsSceneInput } from './types';
import {
    GROMA_LOCALIZER_KIND,
    findAnchoringPlacement,
    parseScenePlacements,
    projectPlacementToLegacyAnchor,
    type CmsLocalizedSceneSource,
    type ScenePlacement,
} from './localizers';

/**
 * Which anchor does this scene have? — the one shared answer.
 *
 * A scene's content is attached to an image-marker anchor or the local
 * stage, and that is a fact of the CMS record (accessor
 * `anchorType === 'Image'` — NOT the lingering `imageUrl`, which the
 * backend can't clear on un-anchor). Marker display, the `.zip` frame
 * conversion on import and export, and anchor attachment all derive from
 * this predicate; consumers must not duplicate it.
 * Full contract: docs-internal/ZIP_FRAME_CONTRACT.md.
 *
 * Types are minimal structural POJOs (same pattern as
 * {@link CmsSceneInput}) so consumers' generated DTOs satisfy them
 * without this package importing any CMS types.
 */

/** The CMS SceneAccessor `anchorType` value for an image-marker scene. */
export const IMAGE_ANCHOR_TYPE = 'Image';

/** Structural subset of a CMS SceneAccessor needed for anchor resolution. */
export interface CmsAccessorInput {
    /** e.g. `"Image"` (marker-anchored) or `"Local"` (Anywhere). */
    anchorType?: string | null;
    /** Marker orientation: `"Horizontal"`, `"Vertical"`, or `"Free"`. */
    orientation?: string | null;
    /** Marker pitch in degrees (rotation about the marker's local X axis). */
    pitchDegrees?: number | null;
}

/**
 * Structural subset of a CMS scene record needed to resolve its anchor.
 *
 * `localizers` (TraceBackend#999) wins when the server sends it: the scene
 * is anchored iff it has an enabled primary image placement. When it is
 * absent/null (an older server), or empty while a legacy `Image` accessor
 * and `imageUrl` remain (the pre-backfill window), the legacy accessor
 * fields decide, exactly as before. See {@link effectiveScenePlacements}.
 */
export interface CmsAnchoredSceneSource extends CmsLocalizedSceneSource {
    imageUrl?: string | null;
    /** Marker width in meters. Some DTOs carry it as a string. */
    imageWidth?: string | number | null;
    sceneAccessors?: CmsAccessorInput[] | null;
}

/**
 * The scene's marker SceneAccessor. Location scenes carry it on an
 * `anchorType === 'Image'` accessor (the CMS enforces that); Anywhere
 * scenes store the same marker fields on their sole accessor, whose
 * anchor is `Local`. Fall back to that single accessor so the marker's
 * orientation/pitch resolve for Anywhere scenes too.
 */
export function findImageAccessor<T extends CmsAccessorInput>(
    accessors: T[] | null | undefined,
): T | undefined {
    return accessors?.find((a) => a.anchorType === IMAGE_ANCHOR_TYPE) ?? accessors?.[0];
}

/**
 * Whether a scene is image-anchored. The accessor's
 * `anchorType === 'Image'` is the single source of truth: the studio +
 * modal save flows promote the accessor to `Image` whenever a marker is
 * present, and un-anchoring demotes it to `Local`. The backend cannot
 * clear the stored `imageUrl` on un-anchor, so the URL lingers and CANNOT
 * be the signal.
 *
 * Trade-off: legacy scenes whose marker was added in-studio before
 * TraceFrontend #2117 sit at `Local` + `imageUrl` and read as un-anchored
 * here until they're re-saved (which heals them to `Image`). Chosen
 * deliberately — with server data alone, "legacy anchored" and "user
 * un-anchored" are indistinguishable until the backend gains an explicit
 * image-clear (`RemoveImage`), after which new records are unambiguous.
 */
export function isSceneAnchored(
    scene: Pick<CmsAnchoredSceneSource, 'sceneAccessors' | 'localizers' | 'imageUrl'> | null | undefined,
): boolean {
    const placements = effectiveScenePlacements(scene);
    if (placements) return findAnchoringPlacement(placements) !== undefined;
    return isLegacyImageAnchored(scene);
}

/** The pre-#999 answer: the scene's marker accessor is `anchorType === 'Image'`. */
function isLegacyImageAnchored(
    scene: Pick<CmsAnchoredSceneSource, 'sceneAccessors'> | null | undefined,
): boolean {
    return findImageAccessor(scene?.sceneAccessors)?.anchorType === IMAGE_ANCHOR_TYPE;
}

/**
 * The placements that decide this scene's anchor, or `null` when the legacy
 * accessor fields decide it. Every "placements present?" branch goes through
 * this (`isSceneAnchored`, `buildCmsSceneInput`, and consumers building the
 * AR tracking list with `trackedLocalizersFromPlacements`).
 *
 * Null when:
 * - `localizers` is absent or null (an older server);
 * - `localizers` holds no usable placement, while the scene still has a
 *   legacy `Image` accessor and a marker `imageUrl`. That is the rollout
 *   window of TraceBackend#999, when the server sends `localizers: []`
 *   until the backfill runs. Treating it as unanchored would drop every
 *   legacy-anchored scene's marker. It stays correct after migration: when
 *   an editor removes a scene's last placement, the backend's projection
 *   demotes the accessor to `Local`, so this reads unanchored.
 *
 * Groma placements never change the answer, because no runtime localizes
 * against a VPS space: they are removed, and the rest of the list decides as
 * above. An editor that draws them reads `parseScenePlacements` instead.
 */
export function effectiveScenePlacements(
    scene: Pick<CmsAnchoredSceneSource, 'sceneAccessors' | 'localizers' | 'imageUrl'> | null | undefined,
): ScenePlacement[] | null {
    const parsed = parseScenePlacements(scene);
    if (!parsed) return null;
    const placements = parsed.filter(p => p.localizer?.kind !== GROMA_LOCALIZER_KIND);
    if (placements.length === 0 && !!scene?.imageUrl && isLegacyImageAnchored(scene)) return null;
    return placements;
}

/**
 * Flatten a scene + its image accessor into the POJO `loadCmsScene`
 * expects. Gated on {@link isSceneAnchored}: an un-anchored scene (or one
 * whose marker was removed) yields an empty input so the viewer renders
 * no marker and applies no frame conversion — the stored `imageUrl` may
 * still be present on the record but must not be honored.
 *
 * By construction of this gate, `imageUrl != null` on the returned
 * `CmsSceneInput` MEANS the scene is anchored — `loadCmsScene` keys its
 * frame conversion and anchor attach off exactly that.
 *
 * `includeDisabledPrimary` lets runtime loading recover the authored basis;
 * it does not change the default editor/export eligibility gate.
 */
export function buildCmsSceneInput(
    scene: CmsAnchoredSceneSource | null | undefined,
    options: { includeDisabledPrimary?: boolean } = {},
): CmsSceneInput {
    const placements = effectiveScenePlacements(scene);
    if (placements) return buildPlacementSceneInput(scene, placements, options.includeDisabledPrimary);

    const accessor = findImageAccessor(scene?.sceneAccessors);
    if (accessor?.anchorType !== IMAGE_ANCHOR_TYPE) {
        return { imageUrl: null, imageWidth: null, imageOrientation: null, imagePitchDegrees: null };
    }
    const width = scene?.imageWidth;
    const imageWidth = typeof width === 'number'
        ? (width || null)
        : (width ? Number(width) || null : null);
    return {
        imageUrl: scene?.imageUrl ?? null,
        imageWidth,
        imageOrientation: accessor.orientation ?? null,
        imagePitchDegrees: accessor.pitchDegrees ?? null,
    };
}

/**
 * {@link buildCmsSceneInput} for a server that sends `localizers`: the
 * marker is the anchoring placement's localizer, and the pose is the
 * placement's. `imageOrientation`/`imagePitchDegrees` still go out for
 * consumers of the legacy fields (the `ImageAnchor` data): the placement's
 * legacy projection when it has one, the accessor's own fields for a
 * `legacy_heuristic` row (the server keeps them for exactly that row), and
 * null when the pose isn't expressible that way.
 */
function buildPlacementSceneInput(
    scene: CmsAnchoredSceneSource | null | undefined,
    placements: readonly ScenePlacement[],
    includeDisabledPrimary = false,
): CmsSceneInput {
    const placement = includeDisabledPrimary
        ? placements.find(p => p.isPrimary && p.localizer?.kind === 'image' && p.localizer.imageUrl)
        : findAnchoringPlacement(placements);
    if (!placement?.localizer) {
        return { imageUrl: null, imageWidth: null, imageOrientation: null, imagePitchDegrees: null, anchorPlacement: null };
    }
    let imageOrientation: string | null = null;
    let imagePitchDegrees: number | null = null;
    if (placement.rotationSource === 'legacy_heuristic') {
        const accessor = findImageAccessor(scene?.sceneAccessors);
        imageOrientation = accessor?.orientation ?? null;
        imagePitchDegrees = accessor?.pitchDegrees ?? null;
    } else {
        const projection = projectPlacementToLegacyAnchor(placement);
        imageOrientation = projection?.orientation ?? null;
        imagePitchDegrees = projection?.pitchDegrees ?? null;
    }
    return {
        imageUrl: placement.localizer.imageUrl,
        imageWidth: placement.localizer.widthM,
        imageOrientation,
        imagePitchDegrees,
        anchorPlacement: {
            id: placement.id,
            t: [...placement.t],
            q: [...placement.q],
            rotationSource: placement.rotationSource,
        },
    };
}
